import { useContext, useEffect, useState } from 'react'
import { Textfit } from "react-textfitfix"

import { PassContext } from "../../../pass-context"
import fieldFormatter from "./field-formatter"
import PopupEditor from '../../popup-editor'
import dataSelector from '../../data-selector'

const LABEL_FONT = 'SFPro'
const VALUE_FONT_FAMILY = 'SFPro'

const FONT_SIZES = {
  generic: {
    primary: { min: 10, max: 30, mode: "multi" },
    secondary: { min: 6, max: 16, mode: "single" },
    auxiliary: { min: 6, max: 12, mode: "single" }
  },

  eventTicket: {
    primary: { min: 10, max: 30, mode: "multi" },
    secondary: { min: 6, max: 16, mode: "single" },
    auxiliary: { min: 6, max: 12, mode: "single" }
  },

  eventTicket_bg: {
    primary: { min: 10, max: 30, mode: "multi" },
    secondary: { min: 6, max: 16, mode: "single" },
    auxiliary: { min: 6, max: 12, mode: "single" }
  },

  coupon: {
    primary: { min: 10, max: 30, mode: "multi" },
    secondary: { min: 6, max: 16, mode: "single" },
  },

  storeCard: {
    primary: { min: 10, max: 30, mode: "multi" },
    secondary: { min: 6, max: 16, mode: "single" },
  },

  boardingPass: {
    primary: { min: 10, max: 30, mode: "multi" },
    secondary: { min: 6, max: 16, mode: "single" },
    auxiliary: { min: 6, max: 12, mode: "single" }
  },
}

const getFontSizes = (passType, subType, fieldType) => {
  const passAndSubType = passType === "eventTicket" && subType === "BACKGROUND" ? "eventTicket_bg" : passType
  return FONT_SIZES[passAndSubType]?.[fieldType] || FONT_SIZES.generic[fieldType]
}

// Cache a canvas context so we can measure text width in the exact font we render.
const measureLabelWidth = (() => {
  let canvas
  let context

  return (text = "", fontSize = 16) => {
    if (typeof document !== "undefined") {
      if (!canvas) {
        canvas = document.createElement("canvas")
        context = canvas.getContext("2d")
      }

      if (context) {
        context.font = `${fontSize}px ${LABEL_FONT}` 
        return context.measureText(text.toUpperCase().toString()).width
      }
    }

    // Fallback approximation if document/context is unavailable (e.g. SSR)
    return text.toString().length * 6
  }
})()

// Measure value width at an explicit font size (used for max width caps).
const measureValueWidth = (() => {
  let canvas
  let context

  return (text = "", fontSize = 16) => {
    if (typeof document !== "undefined") {
      if (!canvas) {
        canvas = document.createElement("canvas")
        context = canvas.getContext("2d")
      }

      if (context) {
        context.font = `${fontSize}px ${VALUE_FONT_FAMILY}`
        return context.measureText(text.toString()).width
      }
    }

    return text.toString().length * (fontSize * 0.5)
  }
})()

const useFontsReady = () => {
  const [fontsReady, setFontsReady] = useState(false)

  useEffect(() => {
    // Wait for font-face load before sizing so Textfit measures with the real glyphs.
    if (typeof document === "undefined" || !document.fonts?.ready) {
      setFontsReady(true)
      return
    }

    let cancelled = false
    document.fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [])

  return fontsReady
}

const Field = ({ field, fieldType, idx, width, margin, fontsReady, fontSizes }) => {
  try {
    const { template, instanceData, scannerInstanceData, model } = useContext(PassContext)

    let labelClasses = "label truncate uppercase"
    let valueClasses = "value"

    // We always right align the second primary field (only present for boarding passes)
    if (fieldType == "primary") {
      labelClasses += idx === 0 ? " text-left" : " text-right"
      valueClasses += idx === 0 ? " text-left" : " text-right"
    } else {
      if (field.textAlignment === "PKTextAlignmentLeft") {
        labelClasses += " text-left"
        valueClasses += " text-left"
      }

      if (field.textAlignment === "PKTextAlignmentRight") {
        labelClasses += " text-right"
        valueClasses += " text-right"
      }

      if (field.textAlignment === "PKTextAlignmentCenter") {
        labelClasses += " text-center"
        valueClasses += " text-center"
      }
    }

    const bgImage = dataSelector({ template, instanceData, scannerInstanceData, path: "meta.images.background" })

    const styles = {
      label: {
        color: bgImage && fieldType === "primary" ? "white" : model.labelColor,
        minHeight: 14
      },
      value: {
        color: bgImage ? "white" : model.foregroundColor
      }
    }

    const path = `${fieldType}Fields[${idx}]`
    const labelpath = `${path}.label`
    const valuePath = `${path}.value`

    const label = dataSelector({ template, instanceData, scannerInstanceData, path: labelpath })
    const value = dataSelector({ template, instanceData, scannerInstanceData, path: valuePath })
    const resolvedValue = fieldFormatter({ field, value })

    return (
      <>
      <div className='flex-grow-0' style={{ width:width }}>
        <div className={labelClasses} style={styles.label}>
          {label}
        </div>
        <div className={valueClasses} style={styles.value}>
          <Textfit
            key={`textfit-${fieldType}-${idx}-${fontsReady ? "ready" : "pending"}`}
            mode={fontSizes.mode}
            min={fontSizes.min}
            max={fontSizes.max}
          >
            {resolvedValue}
          </Textfit>
        </div>
      </div>
      {margin && 
        <div className='flex-grow' style={{ minWidth:margin, height:14 }}/>
      }
      </>
    )
  }
  catch (err) {
    throw err
  }
}

const Fields = ({ maxFields, fieldType, fieldsOverallWidth = 345, fieldsOverallHeight = 40 }) => {
  try {
    const {passType, subType, template, instanceData, scannerInstanceData } = useContext(PassContext)
    const fontsReady = useFontsReady()
    const path = `${fieldType}Fields`

    const fields = template[path]

    if (fields) {
      const fontSizes = getFontSizes(passType, subType, fieldType)

      // Remove any fields that are in excess of those permitted for this current pass type
      const permittedFields = fields?.slice(0, Math.min(maxFields, fields.length))

      const gap = 20;
      const totalGapSize = gap * (permittedFields.length - 1)
      const remainingWidth = fieldsOverallWidth - totalGapSize

      // Strategy:
      // 1) Measure label widths in pixels so they never truncate (min bound).
      // 2) Measure value widths at the max font size for this field set to know
      //    how wide they could be if Textfit chooses the largest size (max bound).
      // 3) Use character counts to apportion the remaining width proportionally
      //    across fields (desired width).
      // 4) Clamp each desired width between its min/max, then redistribute if the
      //    total is over/under the available width.
      // First pass: gather label widths (pixels), value char counts (relative share),
      // and value pixel widths at the max font size (absolute cap).
      const labelWidths = []
      const valueCharCounts = []
      const valuePixelWidths = []

      permittedFields.map((field, idx) => {
        const path = `${fieldType}Fields[${idx}]`
        const labelpath = `${path}.label`
        const valuePath = `${path}.value`

        const label = dataSelector({ template, instanceData, scannerInstanceData, path: labelpath })
        const value = dataSelector({ template, instanceData, scannerInstanceData, path: valuePath })

        const resolvedValue = fieldFormatter({ field, value })

        labelWidths.push(measureLabelWidth(label,fontSizes.max))
        valueCharCounts.push(resolvedValue.toString().length)
        valuePixelWidths.push(measureValueWidth(resolvedValue, fontSizes.max))
      })

      const totalContentLength = valueCharCounts.reduce((total, n) => total + n, 0)
      const baseWidthPerCharacter = totalContentLength ? remainingWidth / totalContentLength : 0

      const baseValueWidths = valueCharCounts.map((w) => baseWidthPerCharacter * w)

      // Per-field constraints:
      // min: fit the label
      // max: fit label or value at the max font size
      const maxWidths = permittedFields.map((_, idx) =>
        Math.max(labelWidths[idx], valuePixelWidths[idx])
      )

      // Desired widths driven by value share, clamped between min/max.
      const desiredWidths = baseValueWidths.map((w, idx) =>
        Math.min(Math.max(w, labelWidths[idx]), maxWidths[idx])
      )
      const desiredTotal = desiredWidths.reduce((total, w) => total + w, 0)

      let lengths = desiredWidths

      if (desiredTotal > remainingWidth) {
        const overBy = desiredTotal - remainingWidth
        const shrinkable = desiredWidths.map((w, idx) => Math.max(0, w - labelWidths[idx]))
        const shrinkPool = shrinkable.reduce((total, w) => total + w, 0)

        if (shrinkPool > 0) {
          lengths = desiredWidths.map((w, idx) => {
            const shrinkShare = shrinkPool ? (shrinkable[idx] / shrinkPool) * overBy : 0
            return Math.max(labelWidths[idx], w - shrinkShare)
          })
        }
      } else if (desiredTotal < remainingWidth) {
        // Redistribute leftover width to fields that are below their max, proportionally.
        let leftover = remainingWidth - desiredTotal
        let current = [...desiredWidths]

        // iterative fill to respect caps
        for (let i = 0; i < 3 && leftover > 0; i++) {
          const expandable = current.map((w, idx) => Math.max(0, maxWidths[idx] - w))
          const expandPool = expandable.reduce((t, w) => t + w, 0)
          if (expandPool === 0) break

          current = current.map((w, idx) => {
            const add = (expandable[idx] / expandPool) * leftover
            return Math.min(maxWidths[idx], w + add)
          })
          const newTotal = current.reduce((t, w) => t + w, 0)
          leftover = remainingWidth - newTotal
        }

        lengths = current
      }

      const classnames = "editable flex flex-row " + (permittedFields.length === 1 ? " justify-start" : "justify-between")

      return (
        <div className={fieldType}>
          <div className="item" style={{ width: fieldsOverallWidth, maxWidth: fieldsOverallWidth, height: fieldsOverallHeight }}>
            <PopupEditor title={fieldType + " Fields"} path={fieldType + "Fields"} colors={{ label: subType === "BACKGROUND" ? "WHITE" : "model.labelColor", value: subType === "BACKGROUND" ? "WHITE" : "model.foregroundColor" }} maxFields={maxFields}>
              <div className={classnames} style={{ maxWidth: fieldsOverallWidth }}>
                {permittedFields?.map((f, idx) => <Field key={f.key} idx={idx} field={f} fieldType={fieldType} width={lengths[idx]} margin={idx < (permittedFields.length-1) ? gap : null} fontsReady={fontsReady} fontSizes={fontSizes}/>)}
              </div>
            </PopupEditor>
          </div>
        </div>
      )
    }
  }
  catch (err) {
    throw err
  }
}

export default Fields
